import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRight, ArrowLeft, Lock, ChevronRight, Mail, Check, Shield, FileText, PlayCircle } from 'lucide-react';
import { API_URL } from '../config';
import '../styles/DigitalSafetyScore.css';

/* ---- DATA ---- */
const QUESTIONS = [
  { id:1, scored:true, guide:'alex', question:'When ads seem to "follow you around" after you browse something once, do you know where to reduce that on Google, Facebook, or Instagram?', answers:[{text:'Yes — and I\'ve already changed those settings',score:2},{text:'I know it should be possible, but I wouldn\'t know where to go',score:1},{text:'No — I didn\'t realise I could reduce that',score:0}], blindSpot:'Ads & tracking', blindSpotDetail:'This can make it easier for ad networks to build detailed profiles about your interests and behaviour.' },
  { id:2, scored:true, guide:'maya', question:'Have you ever checked whether your search activity or AI chat settings are being used for extra personalisation or model improvement?', answers:[{text:'Yes — I\'ve reviewed and changed those settings',score:2},{text:'I\'ve heard of those settings, but I haven\'t checked them properly',score:1},{text:'No — I didn\'t know that was something I could control',score:0}], blindSpot:'Search & AI activity', blindSpotDetail:'This means your conversations and searches may be stored longer or used in ways you didn\'t expect.' },
  { id:3, scored:true, guide:'alex', question:'If someone had your phone number or email address, how confident are you that they could not use it to find your social profiles?', answers:[{text:'Very confident',score:2},{text:'Somewhat confident',score:1},{text:'Not very confident',score:0},{text:'I have no idea',score:0}], blindSpot:'Discoverability', blindSpotDetail:'This can make it easier for strangers or scammers to connect your profiles across platforms.' },
  { id:4, scored:true, guide:'alex', question:'Are your photos and posts private or friends-only by default on the platforms that matter most to you?', answers:[{text:'Yes',score:2},{text:'Some are, some aren\'t',score:1},{text:'No',score:0},{text:'I\'m not sure',score:0}], blindSpot:'Photo privacy', blindSpotDetail:'This means your images could be copied, saved, or reused by people you don\'t know.' },
  { id:5, scored:true, guide:'marcus', question:'When was the last time you checked which apps still have access through "Sign in with Google," "Sign in with Facebook," or "Sign in with Apple"?', answers:[{text:'Within the last 6 months',score:2},{text:'More than 6 months ago',score:1},{text:'I don\'t think I ever have',score:0},{text:'I didn\'t realise old apps could keep access',score:0}], blindSpot:'Old app connections', blindSpotDetail:'This creates extra entry points into your accounts that you may have forgotten about.' },
  { id:6, scored:true, guide:'marcus', question:'If you connect to public Wi-Fi at a cafe, airport, hotel, or shopping centre, do you have a clear protection rule you actually follow?', answers:[{text:'Yes — I know exactly what I do',score:2},{text:'I try to be careful, but I don\'t really have a system',score:1},{text:'No — I usually just connect and use it normally',score:0}], blindSpot:'Public Wi-Fi habits', blindSpotDetail:'This could expose your banking, email, and other sensitive logins on networks you don\'t control.' },
  { id:7, scored:true, guide:'marcus', question:'Do you think of your main email inbox as one of the most important accounts to protect in your digital life?', answers:[{text:'Yes — absolutely',score:2},{text:'Somewhat',score:1},{text:'Not really',score:0},{text:'I\'ve never thought about it that way',score:0}], blindSpot:'Email protection', blindSpotDetail:'Your inbox controls password resets and account recovery — it\'s often the key to everything else.' },
  { id:8, scored:true, guide:'maya', question:'Do you know which apps on your phone currently have access to your microphone?', answers:[{text:'Yes — and I review them from time to time',score:2},{text:'I could probably check, but I haven\'t recently',score:1},{text:'No — I don\'t know',score:0}], blindSpot:'Microphone permissions', blindSpotDetail:'This means apps could be listening even when you\'re not actively using them.' },
  { id:9, scored:false, guide:'maya', learningStyle:true, question:'When changing settings on your phone or accounts, what helps you most?', answers:[{text:'A short written checklist I can work through at my own pace',style:'blueprint'},{text:'A video showing me exactly where to tap',style:'video'},{text:'A mix of both',style:'mixed'}] },
  { id:10, scored:false, guide:'maya', learningStyle:true, question:'If a setting looks slightly different on your device, what are you more likely to prefer?', answers:[{text:'I\'m comfortable figuring it out from written guidance',style:'blueprint'},{text:'I feel better when I can watch someone walk me through it',style:'video'},{text:'Depends on the task',style:'mixed'}] },
];

const SCORE_BANDS = [
  { min:0, max:4, sub:'Your answers show a few gaps in your digital safety setup.', detail:'Your answers suggest there are a few practical settings and habits currently leaving you more exposed than necessary. The good news is that these are exactly the kinds of issues Course 1 is designed to help you fix — calmly, clearly, and step by step.', debrief:'You\'ve got real gaps. That\'s not a judgement — it\'s a starting point. Let\'s fix them.' },
  { min:5, max:9, sub:'You\'ve got some awareness — but a few key protections are likely still missing.', detail:'You\'re not starting from zero, which is great — but there are likely a few settings you know matter without being fully sure where to change them. Course 1 helps turn awareness into action.', debrief:'You\'re not starting from zero. But a few settings need tightening. Let\'s close the gaps.' },
  { min:10, max:13, sub:'You\'ve got good instincts — with a few worthwhile gaps to tighten.', detail:'You\'ve already got some strong instincts in place. A quick structured review of the areas most people overlook could still meaningfully tighten your setup.', debrief:'Solid ground. A few areas could still use a pass, but the foundation\'s there.' },
  { min:14, max:16, sub:'You\'ve already built some strong digital safety habits.', detail:'Your foundations already look stronger than most people\'s. Course 1 is still worthwhile if you want to review the settings that quietly shape your digital exposure and make sure everything is working the way you think it is.', debrief:'You\'re already ahead. A structured check might still surface something useful.' },
];

const MILESTONES = { 2:'Nice.', 4:'Good one.', 6:'You\'re building your score.', 8:'Assessment taking shape.' };

/* ---- THREE RECURRING GUIDES — Realistic Privacy Advisors ---- */
const GUIDES = {
  alex: {
    name: 'Alex',
    role: 'Privacy Specialist',
    avatar: 'https://static.prod-images.emergentagent.com/jobs/a2212a51-8f24-43d7-b4aa-6b215b468650/images/74c7896c7e172cad05cb7070f87b266a8eb23f74e110842d9f02f81790b4dcd4.png',
    lines: {
      1: "Ad tracking can feel invisible until you know where to look.",
      3: "Small details like a phone number can connect more than you'd expect.",
      4: "Your photos tell a story — privacy settings decide who gets to read it."
    }
  },
  maya: {
    name: 'Maya',
    role: 'Data & AI Settings',
    avatar: 'https://static.prod-images.emergentagent.com/jobs/a2212a51-8f24-43d7-b4aa-6b215b468650/images/c621e7325607e31330f064c437d8703498cf4a06a7f601799966b0afd192ea75.png',
    lines: {
      2: "You'd be surprised how much gets logged quietly in the background.",
      8: "A live mic is never a small detail. Worth checking which apps have access.",
      9: "This one's about how you learn — not what you know.",
      10: "Last question. Be honest about what actually helps you follow through."
    }
  },
  marcus: {
    name: 'Marcus',
    role: 'Account Security',
    avatar: 'https://static.prod-images.emergentagent.com/jobs/a2212a51-8f24-43d7-b4aa-6b215b468650/images/12518d5250c1afbf8d1d03a85599ce41902274b9de4a1caf60557f79f2a65893.png',
    lines: {
      5: "Old apps have a way of keeping access you forgot you gave.",
      6: "Public networks are convenient — and worth having a simple rule for.",
      7: "Your inbox is the control centre for your digital life."
    }
  }
};

// Team lineup for attract screen (just the 3 guides)
const TEAM_LINEUP = [
  GUIDES.alex,
  GUIDES.maya,
  GUIDES.marcus
];

/* ---- HOLOGRAPHIC GUIDE (Simple version for processing/results) ---- */
const HoloGuide = ({ size = 'md', state = 'idle' }) => (
  <div className={`holo-guide holo-${size} holo-${state}`} aria-hidden="true">
    <svg viewBox="0 0 120 160" className="holo-svg">
      <defs>
        <linearGradient id="holoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(22,177,196,0.7)" />
          <stop offset="50%" stopColor="rgba(22,177,196,0.3)" />
          <stop offset="100%" stopColor="rgba(22,177,196,0.6)" />
        </linearGradient>
        <filter id="holoGlow"><feGaussianBlur stdDeviation="2" /><feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge></filter>
      </defs>
      <path d="M60,8 L105,35 L105,95 Q105,130 60,152 Q15,130 15,95 L15,35 Z" fill="none" stroke="url(#holoGrad)" strokeWidth="1.5" filter="url(#holoGlow)" className="holo-body" />
      <path d="M60,20 L95,42 L95,92 Q95,120 60,138 Q25,120 25,92 L25,42 Z" fill="rgba(22,177,196,0.03)" stroke="rgba(22,177,196,0.15)" strokeWidth="0.8" />
      <circle cx="60" cy="65" r="14" fill="none" stroke="rgba(22,177,196,0.5)" strokeWidth="1" className="holo-eye-ring" />
      <circle cx="60" cy="65" r="5" fill="rgba(22,177,196,0.6)" className="holo-eye-core" />
    </svg>
    <div className="holo-scanline"></div>
  </div>
);

/* ---- PREMIUM GUIDE WITH REALISTIC AVATAR ---- */
const GuideDisplay = ({ questionIndex, isVisible = true }) => {
  const [showSubtitle, setShowSubtitle] = useState(false);
  const question = QUESTIONS[questionIndex];
  const guideKey = question?.guide || 'alex';
  const guide = GUIDES[guideKey];
  const guideLine = guide.lines[questionIndex + 1] || "Let's take a look at this one.";
  
  useEffect(() => {
    setShowSubtitle(false);
    const subtitleTimer = setTimeout(() => {
      setShowSubtitle(true);
    }, 600);
    return () => clearTimeout(subtitleTimer);
  }, [questionIndex]);

  return (
    <div className={`guide-display ${isVisible ? 'visible' : ''}`} data-testid="guide-display">
      {/* Guide info */}
      <div className="guide-info">
        <span className="guide-label">YOUR GUIDE</span>
        <span className="guide-name">{guide.name}</span>
        <span className="guide-role">{guide.role}</span>
      </div>
      
      {/* Realistic avatar */}
      <div className="guide-avatar-container">
        <div className="guide-avatar-wrap">
          <img 
            src={guide.avatar} 
            alt={`${guide.name} - ${guide.role}`}
            className="guide-avatar-img"
            loading="eager"
          />
          <div className="guide-avatar-glow"></div>
        </div>
      </div>
      
      {/* Guide quote - calm and practical */}
      <div className={`guide-quote ${showSubtitle ? 'visible' : ''}`}>
        <p className="guide-quote-text">"{guideLine}"</p>
      </div>
    </div>
  );
};

/* ---- TEAM LINEUP FOR ATTRACT SCREEN ---- */
const TeamLineup = () => (
  <div className="team-lineup">
    {TEAM_LINEUP.map((guide, i) => (
      <div key={guide.name} className="team-member" style={{ animationDelay: `${i * 0.15}s` }}>
        <div className="team-avatar-wrap">
          <img src={guide.avatar} alt={guide.name} className="team-avatar" loading="eager" />
          <div className="team-avatar-glow"></div>
        </div>
        <span className="team-name">{guide.name}</span>
      </div>
    ))}
  </div>
);

/* ---- MAIN COMPONENT ---- */
const DigitalSafetyScore = ({ onCheckout }) => {
  const [step, setStep] = useState('intro1');
  const [answers, setAnswers] = useState(Array(10).fill(null));
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submittingEmail, setSubmittingEmail] = useState(false);
  const [showEncouragement, setShowEncouragement] = useState(false);
  const [encourageText, setEncourageText] = useState('');
  const [animateScore, setAnimateScore] = useState(false);
  const [displayedScore, setDisplayedScore] = useState(0);
  const [revealPhase, setRevealPhase] = useState(0);
  const [direction, setDirection] = useState('forward');
  const [processingResult, setProcessingResult] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [attractPulse, setAttractPulse] = useState(false);
  const containerRef = useRef(null);

  // Attract mode pulse
  useEffect(() => {
    if (step === 'intro1') {
      const timer = setInterval(() => setAttractPulse(p => !p), 3000);
      return () => clearInterval(timer);
    }
  }, [step]);

  const scrollToQuiz = useCallback(() => {
    if (containerRef.current) {
      const top = containerRef.current.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, []);

  const getQuestionIndex = () => step.startsWith('q') ? parseInt(step.slice(1)) : -1;

  const totalScore = () => {
    let s = 0;
    QUESTIONS.forEach((q, i) => { if (q.scored && answers[i] !== null) s += q.answers[answers[i]].score; });
    return s;
  };

  const getScoreBand = (score) => SCORE_BANDS.find(b => score >= b.min && score <= b.max) || SCORE_BANDS[0];

  const getBlindSpots = () => {
    const spots = [];
    QUESTIONS.forEach((q, i) => {
      if (q.scored && answers[i] !== null && q.answers[answers[i]].score === 0)
        spots.push({ label: q.blindSpot, detail: q.blindSpotDetail });
    });
    if (spots.length < 2)
      QUESTIONS.forEach((q, i) => {
        if (q.scored && answers[i] !== null && q.answers[answers[i]].score === 1 && spots.length < 3 && !spots.find(s => s.label === q.blindSpot))
          spots.push({ label: q.blindSpot, detail: q.blindSpotDetail });
      });
    return spots.slice(0, 3);
  };

  const getFormatRecommendation = () => {
    const q9 = answers[8] !== null ? QUESTIONS[8].answers[answers[8]].style : 'mixed';
    const q10 = answers[9] !== null ? QUESTIONS[9].answers[answers[9]].style : 'mixed';
    if (q9 === 'blueprint' && q10 === 'blueprint') return 'blueprint';
    if (q9 === 'video' && q10 === 'video') return 'video';
    return 'mixed';
  };

  const goToStep = (newStep, dir = 'forward') => {
    setDirection(dir);
    setStep(newStep);
    setSelectedAnswer(null);
    scrollToQuiz();
  };

  useEffect(() => {
    if (!animateScore) return;
    const target = totalScore();
    const duration = 1800;
    const start = performance.now();
    const animate = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      setDisplayedScore(Math.round((1 - Math.pow(1 - progress, 3)) * target));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [animateScore]);

  const selectAnswer = (qIdx, aIdx) => {
    const na = [...answers]; na[qIdx] = aIdx; setAnswers(na);
    setSelectedAnswer(aIdx);
    const qNum = qIdx + 1;
    if (MILESTONES[qNum]) { setEncourageText(MILESTONES[qNum]); setShowEncouragement(true); setTimeout(() => setShowEncouragement(false), 1400); }
    // Auto-advance after 500ms
    setTimeout(() => { 
      setSelectedAnswer(null);
      goToStep(qIdx < 9 ? `q${qIdx + 1}` : 'email'); 
    }, 500);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setEmailError('Please enter a valid email address.'); return; }
    setEmailError(''); setSubmittingEmail(true);
    try { fetch(`${API_URL}/api/blueprint/quiz-lead`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({email, score:totalScore(), recommendation:getFormatRecommendation()}) }).catch(()=>{}); } catch {}
    await new Promise(r => setTimeout(r, 1200));
    setSubmittingEmail(false); setProcessingResult(true);
    await new Promise(r => setTimeout(r, 2400));
    setProcessingResult(false); goToStep('results');
    setTimeout(() => { setRevealPhase(1); setAnimateScore(true); }, 300);
    setTimeout(() => setRevealPhase(2), 2200);
    setTimeout(() => setRevealPhase(3), 3200);
  };

  const handleCTA = (type) => { if (onCheckout) onCheckout(type); else { const p = document.getElementById('pricing'); if (p) p.scrollIntoView({behavior:'smooth'}); } };

  const score = totalScore();
  const band = getScoreBand(score);
  const blindSpots = getBlindSpots();
  const recommendation = getFormatRecommendation();
  const qIdx = getQuestionIndex();
  const scorePercent = Math.round((score / 16) * 100);

  return (
    <div className="dss-module" ref={containerRef} data-testid="digital-safety-score">
      {/* Living background layers */}
      <div className="dss-bg-layer dss-bg-grid"></div>
      <div className="dss-bg-layer dss-bg-scanlines"></div>
      <div className="dss-bg-layer dss-bg-particles">{[...Array(24)].map((_,i) => <span key={i} className="dss-particle" style={{left:`${Math.random()*100}%`,top:`${Math.random()*100}%`,animationDelay:`${Math.random()*8}s`,animationDuration:`${6+Math.random()*8}s`}} />)}</div>
      <div className="dss-bg-layer dss-bg-pulse"></div>
      <div className="dss-bg-layer dss-bg-glow"></div>
      {step === 'intro1' && <div className="dss-bg-layer dss-bg-arcs"><div className="dss-arc a1"></div><div className="dss-arc a2"></div></div>}

      <div className="dss-inner">
        {/* Progress bar */}
        {(qIdx >= 0 || step === 'email') && (
          <div className="dss-progress-bar" data-testid="dss-progress">
            <div className="dss-progress-segments">{[...Array(10)].map((_,i) => <div key={i} className={`dss-segment ${i<(qIdx>=0?qIdx+1:10)?'filled':''} ${i===qIdx?'current':''}`} />)}</div>
            <span className="dss-progress-label">{step === 'email' ? 'Assessment complete' : `Question ${qIdx+1} of 10`}</span>
            {qIdx >= 0 && qIdx < 10 && qIdx % 3 === 2 && (
              <span className="dss-progress-hint">Your safety score is updating as you go.</span>
            )}
          </div>
        )}

        {/* Encouragement */}
        <div className={`dss-encourage ${showEncouragement?'visible':''}`}><span className="dss-encourage-dot"></span>{encourageText}</div>

        {/* Processing overlay */}
        {processingResult && (
          <div className="dss-processing">
            <div className="dss-processing-card">
              <HoloGuide size="lg" state="processing" />
              <p className="dss-processing-text">Analysing your results</p>
              <div className="dss-processing-dots"><span /><span /><span /></div>
            </div>
          </div>
        )}

        {/* ===== ATTRACT MODE INTRO ===== */}
        {step === 'intro1' && (
          <div className={`dss-card dss-attract ${direction==='forward'?'enter-right':'enter-left'} ${attractPulse?'pulse':''}`}>
            {/* Team lineup */}
            <TeamLineup />
            
            <span className="dss-eyebrow">2-MINUTE DIGITAL SAFETY CHECK</span>
            <p className="dss-benchmark">Most people score between 4 and 7 out of 16 on their first check.</p>
            <h2 className="dss-title">How Exposed Is Your<br/>Digital Life?</h2>
            <p className="dss-subtitle">Answer 10 quick questions to see where your accounts, data, and devices may be more visible than you expect.</p>
            
            <div className="dss-value-bullets">
              <p className="dss-value-intro">You'll get:</p>
              <ul>
                <li><Check size={16} /> your <strong>Digital Safety Score</strong></li>
                <li><Check size={16} /> the <strong>areas where you're most exposed</strong></li>
                <li><Check size={16} /> the <strong>settings worth checking first</strong></li>
              </ul>
            </div>
            
            <p className="dss-credibility">Based on the same checks we use in Course 1.</p>
            
            <button className="dss-btn-primary dss-btn-ignite" onClick={() => goToStep('preview')} data-testid="dss-start-btn">
              <span className="dss-btn-text">Start the Safety Check</span><ArrowRight size={18} /><span className="dss-btn-shine"></span>
              <span className="dss-btn-glow-ring"></span>
            </button>
            <span className="dss-micro">10 questions • about 2 minutes</span>
            <span className="dss-micro-secondary">Your safety score updates as you go.</span>
          </div>
        )}

        {/* ===== PREVIEW / COMMITMENT STEP ===== */}
        {step === 'preview' && (
          <div className={`dss-card dss-preview ${direction==='forward'?'enter-right':'enter-left'}`}>
            <h2 className="dss-title-sm">Before we start</h2>
            <p className="dss-body">This quick check looks at <strong>10 common settings that affect your digital exposure</strong>, including:</p>
            
            <div className="dss-topic-grid">
              {['ad tracking','profile discoverability','old app permissions','public Wi-Fi habits','email and account protection'].map((t,i) => (
                <span key={i} className="dss-topic-chip" style={{animationDelay:`${0.15+i*0.07}s`}}>{t}</span>
              ))}
            </div>
            
            <div className="dss-preview-outcomes">
              <p>At the end you'll see:</p>
              <ul>
                <li><Check size={16} /> your <strong>Digital Safety Score</strong></li>
                <li><Check size={16} /> the <strong>areas where you're most exposed</strong></li>
                <li><Check size={16} /> the <strong>settings worth checking first</strong></li>
              </ul>
            </div>
            
            <div className="dss-preview-actions">
              <button className="dss-btn-back" onClick={() => goToStep('intro1','back')}><ArrowLeft size={16} /> Back</button>
              <button className="dss-btn-primary dss-btn-ignite" onClick={() => goToStep('q0')} data-testid="dss-begin-btn">
                <span className="dss-btn-text">Start the Safety Check</span><ChevronRight size={18} /><span className="dss-btn-shine"></span>
              </button>
            </div>
          </div>
        )}

        {/* ===== QUESTIONS ===== */}
        {qIdx >= 0 && qIdx <= 9 && (
          <div className={`dss-card dss-question dss-question-with-guide ${direction==='forward'?'enter-right':'enter-left'}`} key={`q-${qIdx}`}>
            <GuideDisplay questionIndex={qIdx} isVisible={true} />
            <p className="dss-q-text">{QUESTIONS[qIdx].question}</p>
            <p className="dss-helper">Choose the option that sounds most like you.</p>
            <div className="dss-answers">
              {QUESTIONS[qIdx].answers.map((a, ai) => (
                <button 
                  key={ai} 
                  className={`dss-answer-btn ${answers[qIdx]===ai?'selected':''} ${selectedAnswer===ai?'just-selected':''}`}
                  onClick={() => selectAnswer(qIdx, ai)} 
                  style={{animationDelay:`${0.12+ai*0.08}s`}} 
                  data-testid={`dss-answer-${qIdx}-${ai}`}
                  disabled={selectedAnswer !== null}
                >
                  <span className="dss-answer-indicator">{String.fromCharCode(65+ai)}</span>
                  <span className="dss-answer-text">{a.text}</span>
                  {(answers[qIdx]===ai || selectedAnswer===ai) && <Check size={18} className="dss-answer-check" />}
                  <span className="dss-answer-ripple"></span>
                </button>
              ))}
            </div>
            <div className="dss-q-footer">
              {qIdx > 0 && <button className="dss-btn-back" onClick={() => goToStep(`q${qIdx-1}`,'back')}><ArrowLeft size={16}/> Back</button>}
              <span className="dss-score-note">Question {qIdx + 1} of 10</span>
            </div>
          </div>
        )}

        {/* ===== EMAIL GATE ===== */}
        {step === 'email' && !processingResult && (
          <div className={`dss-card dss-email-gate ${direction==='forward'?'enter-right':'enter-left'}`}>
            <div className="dss-email-hero"><HoloGuide size="md" state="unlocking" /></div>
            <div className="dss-lock-emblem"><div className="dss-lock-pulse"></div><Lock size={26} strokeWidth={1.5} /></div>
            <h2 className="dss-title-sm">Unlock Your Score</h2>
            <p className="dss-body">You're one step away from seeing your Digital Safety Score, your biggest blind spots, and the learning format we'd recommend for you.</p>
            <form onSubmit={handleEmailSubmit} className="dss-email-form">
              <label className="dss-label" htmlFor="dss-email">Email address</label>
              <div className="dss-input-wrap">
                <Mail size={16} className="dss-input-icon" />
                <input id="dss-email" type="email" placeholder="Enter your email" value={email} onChange={(e) => {setEmail(e.target.value);setEmailError('');}} className={`dss-input ${emailError?'error':''}`} data-testid="dss-email-input" autoComplete="email" />
              </div>
              {emailError && <span className="dss-error">{emailError}</span>}
              <button type="submit" className="dss-btn-primary" disabled={submittingEmail} data-testid="dss-show-results-btn">
                {submittingEmail ? <span className="dss-btn-text">Submitting<span className="dss-loading-dots"><span>.</span><span>.</span><span>.</span></span></span>
                  : <><span className="dss-btn-text">Show My Results</span><ArrowRight size={18}/><span className="dss-btn-shine"></span></>}
              </button>
            </form>
            <p className="dss-micro">We'll also send your results so you can come back to them later.</p>
            <p className="dss-reassurance">No spam. Just your results and practical next steps.</p>
            <button className="dss-btn-back dss-email-back" onClick={() => goToStep('q9','back')}><ArrowLeft size={16}/> Back</button>
          </div>
        )}

        {/* ===== RESULTS ===== */}
        {step === 'results' && !processingResult && (
          <div className="dss-results">
            {revealPhase >= 1 && (
              <div className="dss-card dss-score-card reveal-entrance">
                <div className="dss-results-guide"><HoloGuide size="sm" state="debriefing" /></div>
                <span className="dss-eyebrow">YOUR DIGITAL SAFETY SCORE</span>
                <div className={`dss-score-ring ${animateScore?'animate':''}`}>
                  <svg viewBox="0 0 140 140" className="dss-ring-svg">
                    <defs>
                      <linearGradient id="scoreGrad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#D4A843"/><stop offset="100%" stopColor="#F0D78C"/></linearGradient>
                      <filter id="scoreGlow"><feGaussianBlur stdDeviation="3" result="blur"/><feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
                    </defs>
                    <circle cx="70" cy="70" r="60" className="dss-ring-bg"/>
                    <circle cx="70" cy="70" r="60" className="dss-ring-fill" style={{strokeDashoffset:animateScore?`${377-(score/16)*377}`:'377'}} filter="url(#scoreGlow)"/>
                  </svg>
                  <div className="dss-score-value">
                    <span className="dss-score-num">{displayedScore}</span>
                    <span className="dss-score-max">/16</span>
                    <span className="dss-score-percent">({scorePercent}%)</span>
                  </div>
                  <div className="dss-score-bloom"></div>
                </div>
                <p className="dss-score-context">The average score is 9 / 16.</p>
                <p className="dss-score-basis">Based on your answers to 10 privacy and security checks.</p>
                <h2 className="dss-result-headline">{band.sub}</h2>
                <p className="dss-debrief-line">"{band.debrief}"</p>
                <p className="dss-result-detail">{band.detail}</p>
              </div>
            )}
            
            {revealPhase >= 2 && blindSpots.length > 0 && (
              <div className="dss-card dss-blindspots reveal-entrance">
                <h3 className="dss-section-title">Your biggest blind spots</h3>
                <div className="dss-spot-cards">{blindSpots.map((spot,i) => (
                  <div key={i} className="dss-spot-card" style={{animationDelay:`${i*0.2}s`}}>
                    <div className="dss-spot-accent"></div>
                    <span className="dss-spot-label">{spot.label}</span>
                    <p className="dss-spot-detail">{spot.detail}</p>
                  </div>
                ))}</div>
              </div>
            )}
            
            {revealPhase >= 3 && (<>
              {/* Course bridge explanation */}
              <div className="dss-card dss-bridge-section reveal-entrance">
                <h3 className="dss-section-title">What fixing these usually looks like</h3>
                <p className="dss-body">Most of the gaps above come from <strong>default settings that were never adjusted</strong>.</p>
                <p className="dss-body">In Course 1 we walk through the <strong>exact screens where these controls live</strong> and the few changes that reduce the most exposure.</p>
                <p className="dss-body">Most people complete the key fixes <strong>in under an hour</strong>.</p>
                <p className="dss-body-emphasis">No technical experience required.</p>
              </div>
              
              {/* Product recommendation */}
              <div className="dss-card dss-recommendation reveal-entrance" style={{animationDelay:'0.15s'}}>
                <h3 className="dss-rec-title">Two ways to fix these gaps</h3>
                
                <div className="dss-rec-cards">
                  {/* PDF Blueprint Card */}
                  <div className="dss-rec-card">
                    <div className="dss-rec-card-header">
                      <FileText size={24} />
                      <span className="dss-rec-popular">Most people start here</span>
                    </div>
                    <h4>PDF Blueprint</h4>
                    <p className="dss-rec-price">$28 <span>AUD</span></p>
                    <p className="dss-rec-best-for">Best if you're comfortable changing settings yourself.</p>
                    <ul className="dss-rec-features">
                      <li><Check size={14} /> follow a checklist</li>
                      <li><Check size={14} /> go screen-by-screen at your own pace</li>
                      <li><Check size={14} /> quick reference you can revisit</li>
                    </ul>
                    <button className="dss-btn-primary dss-btn-cta" onClick={()=>handleCTA('pdf')} data-testid="dss-cta-blueprint">
                      <span className="dss-btn-text">Fix these with the Blueprint — $28</span><ArrowRight size={18}/><span className="dss-btn-shine"></span>
                    </button>
                  </div>
                  
                  {/* Video Course Card */}
                  <div className="dss-rec-card">
                    <div className="dss-rec-card-header">
                      <PlayCircle size={24} />
                    </div>
                    <h4>Guided Video Course</h4>
                    <p className="dss-rec-price">$88 <span>AUD</span></p>
                    <p className="dss-rec-best-for">Best if you'd rather follow along step-by-step.</p>
                    <ul className="dss-rec-features">
                      <li><Check size={14} /> see exactly where to tap</li>
                      <li><Check size={14} /> watch and apply the settings</li>
                      <li><Check size={14} /> fewer mistakes and faster setup</li>
                      <li><Check size={14} /> includes the Blueprint</li>
                    </ul>
                    <button className="dss-btn-secondary dss-btn-cta" onClick={()=>handleCTA('videos')} data-testid="dss-cta-videos">
                      Fix these with the Video Course — $88 <ArrowRight size={18}/>
                    </button>
                  </div>
                </div>
                
                <p className="dss-upgrade-note"><Shield size={14} /> Start with the Blueprint and upgrade to the video later — your $28 is credited.</p>
              </div>
              
              <div className="dss-card dss-final-block reveal-entrance" style={{animationDelay:'0.3s'}}>
                <h3 className="dss-section-title">A safer setup doesn't have to be complicated</h3>
                <p className="dss-body">You do not need to become a technical person to feel more protected online. Course 1 is designed to help you reduce your digital exposure through clear, practical changes you can actually make — without overwhelm, jargon, or fear-based messaging.</p>
                <button className="dss-btn-primary dss-btn-cta" onClick={()=>handleCTA(recommendation==='video'?'videos':'pdf')} data-testid="dss-cta-final"><span className="dss-btn-text">Take the Next Step</span><ArrowRight size={18}/><span className="dss-btn-shine"></span></button>
              </div>
            </>)}
          </div>
        )}
      </div>
    </div>
  );
};

export default DigitalSafetyScore;
